import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookCard, BookForm, FilterPanel, SearchInput } from '@/components/books'
import type { BookFilters } from '@/components/books/FilterPanel'
import { Button, ConfirmDialog, Modal, Pagination } from '@/components/ui'
import { useBooksWithFilters, useCreateBook, useDeleteBook, useUpdateBook } from '@/hooks/books'
import { useUpdateBookGenres } from '@/hooks/genres'
import { useDebounce } from '@/hooks/useDebounce'
import { useFilterPersistence } from '@/hooks/useFilterPersistence'
import { captureError } from '@/lib/monitoring'
import type { Book, CreateBook } from '@/types'

export function DigitalShelfPage() {
  const { t } = useTranslation()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingBook, setEditingBook] = useState<Book | null>(null)
  const [bookToDelete, setBookToDelete] = useState<Book | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const pageSize = 20

  // Load persisted filters for digital shelf
  const { filters, updateFilters, isInitialized } = useFilterPersistence('digitalShelf')

  // Debounced search to avoid excessive API calls
  const [searchInput, setSearchInput] = useState(filters.search)
  const debouncedSearch = useDebounce(searchInput, 600)

  // Use database-level filtering and pagination for digital books
  const {
    data: booksData,
    isLoading,
    error: queryError,
  } = useBooksWithFilters({
    filters: {
      ...filters,
      search: debouncedSearch,
      statuses: filters.statuses.length > 0 ? filters.statuses : ['unread', 'reading', 'read'],
      type: 'digital', // Filter only digital books
    },
    page: currentPage,
    pageSize,
    enabled: isInitialized,
  })

  const createBookMutation = useCreateBook()
  const updateBookMutation = useUpdateBook()
  const updateBookGenresMutation = useUpdateBookGenres()
  const deleteBookMutation = useDeleteBook()

  // Reset to page 1 when filters change
  const handleFiltersChange = (newFilters: BookFilters) => {
    const filtersChanged =
      JSON.stringify(filters.genres) !== JSON.stringify(newFilters.genres) ||
      JSON.stringify(filters.statuses) !== JSON.stringify(newFilters.statuses) ||
      filters.ratingRange.min !== newFilters.ratingRange.min ||
      filters.ratingRange.max !== newFilters.ratingRange.max ||
      filters.readYear !== newFilters.readYear ||
      filters.sortBy !== newFilters.sortBy ||
      filters.sortOrder !== newFilters.sortOrder

    if (filtersChanged) {
      updateFilters(newFilters)
      setCurrentPage(1)
    }
  }

  const handleSearchChange = (search: string) => {
    setSearchInput(search)
  }

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      updateFilters({ ...filters, search: debouncedSearch })
      setCurrentPage(1)
    }
  }, [debouncedSearch, filters, updateFilters])

  // Keep searchInput in sync with filters
  useEffect(() => {
    setSearchInput(filters.search)
  }, [filters.search])

  const handleAddBook = async (data: CreateBook, genreIds: string[]) => {
    try {
      setError(null)
      const newBook = await createBookMutation.mutateAsync(data)

      // Update genres if any
      if (genreIds.length > 0) {
        await updateBookGenresMutation.mutateAsync({
          bookId: newBook.id,
          genreIds,
        })
      }

      setShowAddForm(false)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to create digital book' })
      setError(error instanceof Error ? error.message : 'Failed to add digital book')
    }
  }

  const handleUpdateBook = async (data: CreateBook, genreIds: string[]) => {
    if (!editingBook) return

    try {
      setError(null)

      // Update book data
      await updateBookMutation.mutateAsync({
        bookId: editingBook.id,
        data: {
          title: data.title,
          author: data.author,
          isbn: data.isbn,
          status: data.status,
          rating: data.rating,
          notes: data.notes,
          cover_url: data.cover_url,
          start_date: data.start_date,
          end_date: data.end_date,
          ownership: data.ownership,
          borrowed_from: data.borrowed_from,
        },
      })

      // Update genres
      await updateBookGenresMutation.mutateAsync({
        bookId: editingBook.id,
        genreIds,
      })

      setEditingBook(null)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to update digital book' })
      setError(error instanceof Error ? error.message : 'Failed to update digital book')
    }
  }

  const handleEditBook = (book: Book) => {
    setEditingBook(book)
  }

  const handleDeleteBook = (book: Book) => {
    setBookToDelete(book)
  }

  const confirmDeleteBook = async () => {
    if (!bookToDelete) return

    try {
      setError(null)
      await deleteBookMutation.mutateAsync(bookToDelete.id)
      setBookToDelete(null)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to delete digital book' })
      setError(error instanceof Error ? error.message : 'Failed to delete digital book')
    }
  }

  const books = booksData?.books || []
  const totalCount = booksData?.totalCount || 0
  const pageCount = booksData?.pageCount || 0
  const displayError = error || queryError

  if (displayError) {
    return (
      <div className="p-8 text-center">
        <p className="text-error">{t('errors.general')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('navigation.digitalShelf')}</h1>
          <p className="text-muted-foreground mt-1">
            {!isInitialized || isLoading
              ? t('common.loading')
              : totalCount > 0
                ? t('digitalShelf.booksCount', { count: totalCount })
                : t('digitalShelf.yourCollection')}
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(true)}
          className="w-full sm:w-auto"
          disabled={showAddForm}
        >
          {t('digitalShelf.addBook')}
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar and Filter Toggle */}
        <div className="flex gap-3">
          <SearchInput
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={t('search.searchByTitleAuthor')}
            className="flex-1"
          />

          <FilterPanel filters={filters} onFiltersChange={handleFiltersChange} compact={true} />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && books.length === 0 && (
        <div className="text-center py-20">
          <svg
            className="mx-auto h-16 w-16 text-muted-foreground mb-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25"
            />
          </svg>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {searchInput || filters.genres.length > 0 || filters.statuses.length > 0
              ? t('common.noResultsMessage')
              : t('digitalShelf.noBooks')}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchInput || filters.genres.length > 0 || filters.statuses.length > 0
              ? t('books.tryAdjustingFilters')
              : t('digitalShelf.startAddingBooks')}
          </p>
          {!searchInput && filters.genres.length === 0 && filters.statuses.length === 0 && (
            <Button onClick={() => setShowAddForm(true)}>{t('digitalShelf.addFirstBook')}</Button>
          )}
        </div>
      )}

      {/* Books Grid */}
      {!isLoading && books.length > 0 && (
        <>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {books.map((book) => (
              <BookCard
                key={book.id}
                book={book}
                onEdit={handleEditBook}
                onDelete={handleDeleteBook}
              />
            ))}
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="mt-8">
              <Pagination
                currentPage={currentPage}
                totalPages={pageCount}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      {/* Add Book Modal */}
      <Modal
        isOpen={showAddForm}
        onClose={() => setShowAddForm(false)}
        title={t('digitalShelf.addBook')}
      >
        <BookForm
          onSubmit={handleAddBook}
          onCancel={() => setShowAddForm(false)}
          initialData={{ type: 'digital' }}
        />
      </Modal>

      {/* Edit Book Modal */}
      <Modal
        isOpen={!!editingBook}
        onClose={() => setEditingBook(null)}
        title={t('digitalShelf.editBook')}
      >
        {editingBook && (
          <BookForm
            initialData={{
              title: editingBook.title,
              author: editingBook.author,
              isbn: editingBook.isbn || '',
              status: editingBook.status,
              type: editingBook.type || 'digital',
              rating: editingBook.rating || undefined,
              notes: editingBook.notes || '',
              cover_url: editingBook.cover_url || undefined,
              start_date: editingBook.start_date || undefined,
              end_date: editingBook.end_date || undefined,
              ownership: editingBook.ownership || 'owned',
              borrowed_from: editingBook.borrowed_from || undefined,
            }}
            initialGenres={editingBook?.book_genres?.map((bg) => bg.genre.id) || []}
            onSubmit={handleUpdateBook}
            onCancel={() => setEditingBook(null)}
          />
        )}
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!bookToDelete}
        onCancel={() => setBookToDelete(null)}
        onConfirm={confirmDeleteBook}
        title={t('books.confirmDelete.title')}
        message={t('books.confirmDelete.message', { title: bookToDelete?.title })}
      />
    </div>
  )
}
